import { useEffect, useRef, useState } from "react";
import { Check, Circle, Loader2 } from "lucide-react";

const DEFAULT_STAGES = [
  "1. Auditando tempestividade do prazo decadencial (Art. 49 CDC)...",
  "2. Enquadrando dispositivos da Lei do Distrato (Lei nº 13.786/18)...",
  "3. Compilando Notificação Extrajudicial e requerimento de estorno...",
];
const TOTAL_MS = 2500;
const SETTLE_MS = 150;
const WAITING_CAP = 95;

interface Props {
  ready: boolean;
  onDone: () => void;
  stages?: string[];
}

export function ProcessingScreen({ ready, onDone, stages: STAGES = DEFAULT_STAGES }: Props) {
  const [elapsed, setElapsed] = useState(0);
  const [finished, setFinished] = useState(false);
  const doneRef = useRef(onDone);
  const readyRef = useRef(ready);
  doneRef.current = onDone;
  readyRef.current = ready;

  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    let timeout: number | undefined;
    const tick = (now: number) => {
      const e = now - start;
      setElapsed(Math.min(TOTAL_MS, e));
      if (e >= TOTAL_MS && readyRef.current) {
        setFinished(true);
        timeout = window.setTimeout(() => doneRef.current(), SETTLE_MS);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      if (timeout !== undefined) window.clearTimeout(timeout);
    };
  }, []);

  const progress = finished ? 100 : Math.min(WAITING_CAP, Math.round((elapsed / TOTAL_MS) * 100));
  const active = finished ? STAGES.length : Math.min(STAGES.length - 1, Math.floor(elapsed / (TOTAL_MS / STAGES.length)));

  return (
    <div data-testid="processing-screen" role="status" aria-live="polite" className="fade-up py-4">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-lg font-semibold text-slate-100">Processando seu protocolo</h3>
        <span data-testid="processing-progress-value" className="font-mono text-sm text-emerald-400">{progress}%</span>
      </div>
      <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-slate-800" aria-hidden="true">
        <div data-testid="processing-progress-bar" className="h-full rounded-full bg-emerald-500" style={{ width: `${progress}%` }} />
      </div>
      <ol className="mt-8 space-y-4">
        {STAGES.map((label, i) => {
          const state = i < active ? "done" : i === active ? "running" : "pending";
          return (
            <li key={label} data-testid={`processing-stage-${i + 1}`} data-state={state} className="flex items-start gap-3">
              <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border ${state === "done" ? "border-emerald-500 bg-emerald-500 text-slate-950" : state === "running" ? "border-emerald-500/60 text-emerald-400" : "border-slate-700 text-slate-600"}`}>
                {state === "done" && <Check aria-hidden="true" className="h-3.5 w-3.5" />}
                {state === "running" && <Loader2 aria-hidden="true" className="h-3.5 w-3.5 motion-safe:animate-spin" />}
                {state === "pending" && <Circle aria-hidden="true" className="h-2 w-2" />}
              </span>
              <span className={`text-sm leading-relaxed ${state === "pending" ? "text-slate-500" : "text-slate-200"}`}>{label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
